import React from 'react';
import { Box } from '@mui/system';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  IComment,
  IMembership,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useMembershipCommentsQuery
} from '../../generated/graphql';
import { updateMembershipInCache } from '../../utils/clientUtils';
import { Colors } from '../../utils/theme';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Pagination,
  TextField
} from '@mui/material';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import Comment from './Comment';

const commentsPerPage = 4;

type IProps = {
  handleClose: () => void;
  membership: IMembership | null;
  updateCommentsCount?: (isRemoved: boolean) => void;
};

const CommentsList: React.FunctionComponent<IProps> = ({
  handleClose,
  membership,
  updateCommentsCount
}: IProps) => {
  const { dispatch } = useContext(AppContext);
  const { data, loading } = useMembershipCommentsQuery({
    variables: { membership_id: parseInt(membership?.id as string) },
    fetchPolicy: 'no-cache'
  });
  const [addComment, addCommentResult] = useAddCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();
  const [commentsCount, setCommentsCount] = useState(membership?.comments_count);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<IComment[]>([]);
  const [pageComments, setPageComments] = useState<IComment[]>([]);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const isLoading = loading || addCommentResult.loading;

  useEffect(() => {
    if (data && data.result) {
      const comments = data.result;
      setComments(comments as IComment[]);
      setPages(
        comments.length > commentsPerPage ? Math.ceil(comments.length / commentsPerPage) : 0
      );
      setPageComments(comments.slice(0, commentsPerPage) as IComment[]);
    }
  }, [data?.result]);

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey) {
        setNewComment(newComment + '\n');
      } else {
        addNewComment();
      }
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    const start = (value - 1) * commentsPerPage;
    setPageComments(comments.slice(start, start + commentsPerPage));
    setCurrentPage(value);
  };

  const handleRemove = (comment: IComment) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete the following comment: ${comment.text}`,
        agreeAction: removeComment,
        params: comment
      }
    });
  };

  const removeComment = (comment: IComment) => {
    deleteComment({
      variables: { id: parseInt(comment.id), membership_id: parseInt(membership?.id as string) },
      update(_, { data }) {
        if (data?.result) {
          updateMembershipInCache(data.result as IMembership);
          updateComments(comment, false);
          if (updateCommentsCount) {
            updateCommentsCount(true);
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Deleted ${comment.text} comment`,
              autoHide: 3000
            }
          });
        }
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
    dispatch({
      type: Actions.HideConfirmation
    });
  };

  const addNewComment = () => {
    if (newComment.trim().length === 0) {
      setNewComment('');
      return;
    }
    addComment({
      variables: {
        membership_id: parseInt(membership?.id as string),
        text: newComment
      },
      update(_, { data }) {
        if (data?.result) {
          updateMembershipInCache(data.result.membership as IMembership);
          updateComments(data.result.comment as IComment, true);
          setNewComment('');
          if (updateCommentsCount) {
            updateCommentsCount(false);
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Comment ${data.result.comment.text} added`
            }
          });
        }
      }
    }).catch((error) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      });
    });
  };

  const updateComments = (comment: IComment, isNew: boolean) => {
    const newCommentsList = isNew
      ? [comment, ...comments]
      : comments.filter((c) => c.id !== comment.id);
    const pageCount =
      newCommentsList.length <= commentsPerPage
        ? 0
        : Math.ceil(newCommentsList.length / commentsPerPage);
    const newCurrentPage =
      pageCount === 0 ? 1 : currentPage > pageCount ? currentPage - 1 : currentPage;
    setComments(newCommentsList);
    setCommentsCount(newCommentsList.length);
    const start = (newCurrentPage - 1) * commentsPerPage;
    setPageComments(newCommentsList.slice(start, start + commentsPerPage));
    setPages(pageCount);
    setCurrentPage(newCurrentPage);
  };

  return (
    <Dialog
      id="comment-dialog"
      open={true}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          display: 'flex'
        }
      }}
      PaperProps={{
        sx: {
          m: 0,
          maxWidth: '600px',
          width: '600px',
          height: '92.6%'
        }
      }}
      BackdropProps={{ sx: { background: Colors.modalBackground } }}
      onClose={handleClose}
    >
      <DialogTitle
        id="comment-title"
        style={{
          borderBottom: '1px solid ' + Colors.skyBlue,
          textAlign: 'center',
          paddingTop: '7px',
          paddingBottom: '21px',
          height: '50px'
        }}
      >
        <Box
          id="comment-count"
          data-testid="comment-count"
          component="span"
          sx={{
            margin: 'auto',
            color: Colors.skyBlue,
            letterSpacing: '-0.01px',
            fontSize: '22px',
            fontWeight: 'bold'
          }}
        >
          Comments ({commentsCount})
        </Box>
      </DialogTitle>
      <DialogTitle
        id="comment-dialog-title"
        style={{
          backgroundColor: '#F4F9FE',
          textAlign: 'center',
          paddingTop: '8px',
          paddingBottom: '13px'
        }}
      >
        <Box
          id="comment-membership-name"
          data-testid="comment-membership-name"
          component="span"
          sx={{
            margin: 'auto',
            color: Colors.main,
            letterSpacing: '-0.01px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {membership?.name}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(1, minmax(0, 1fr))'
            }}
          >
            <Box sx={{ padding: '5% 6.66%' }}>
              <TextField
                id="comments-new-comment"
                label="Write a comment"
                name="comments-new-comment"
                variant="standard"
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
                multiline
                rows={5}
                fullWidth
                onKeyPress={handleKeyPressed}
                InputLabelProps={{
                  style: { color: Colors.main }
                }}
                inputProps={{
                  style: {
                    color: Colors.main
                  },
                  'data-testid': 'comments-new-comment-input-field'
                }}
                disabled={isLoading}
              />
            </Box>
            {pageComments.map((comment, index) => (
              <Box
                sx={{
                  borderTop: index === 0 ? '1px solid #6CA6F2' : '',
                  borderBottom: '1px solid #6CA6F2',
                  backgroundColor: index % 2 === 0 ? '#F4F9FE' : 'white'
                }}
              >
                <Comment comment={comment} remove={handleRemove} index={index + 1} />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      {pages > 0 && (
        <DialogActions sx={{ justifyContent: 'start' }}>
          <Pagination
            id="comment-pagination"
            data-testid="comment-pagination"
            count={pages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </DialogActions>
      )}
      <DialogActions sx={{ marginBottom: '30px', justifyContent: 'center', gap: '20px' }}>
        <CancelButton
          id="cancel-comment-button"
          data-testid="cancel-comment-button"
          onClick={handleClose}
          disabled={isLoading}
          text="Cancel"
        />
        <SaveButton
          id="save-comment-button"
          data-testid="save-comment-button"
          onClick={addNewComment}
          disabled={isLoading}
          text="Save"
        />
      </DialogActions>
    </Dialog>
  );
};

export default CommentsList;
